import React, { useCallback, useMemo } from 'react';

import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { OfferType } from 'constants/enums';
import { useGamePortalPagesState } from 'state/hooks/game-portal-pages.hook';
import { z } from 'zod';

import AcSelect from 'components/AcSelect/AcSelect';
import { cn } from 'components/lib/utils';

import InputSelectDropdown, {
  InputSelectDropdownOption
} from '../../../../../../components/InputSelectDropdown/InputSelectDropdown';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../../../components/ui/form';
import { ERROR_FIELD_CLASS, IBlockContentProps } from '../../../constants';
import { offerBlockSchema, pageFormSchema } from '../../../validation-schema';
import { BlockSegmentsField } from '../Fields/BlockSegmentsField';
import { BlockVisibilityField } from '../Fields/BlockVisibilityField';

const ChevronDownIcon = (props: { className?: string }) => (
  <FontAwesomeIcon
    icon="fa-solid fa-chevron-down text-xs mr-[9px] mt-[-2px]"
    className={cn('text-base-foreground text-[#404040]', props.className)}
  />
);

type OfferBlockType = z.infer<typeof offerBlockSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type OfferFieldProps<T extends keyof OfferBlockType> = ControllerRenderProps<
  PageFormType,
  any
> & {
  value: OfferBlockType[T];
};

export const OfferBlockForm: React.FC<IBlockContentProps> = ({
  block,
  blockIndex
}) => {
  const { control, watch, getFieldState, resetField } =
    useFormContext<z.infer<typeof pageFormSchema>>();
  const { storeOffers } = useGamePortalPagesState();

  const basePath = `blocksV2.${blockIndex}` as const;
  const currentOfferType = watch(`${basePath}.offerType`);

  const getFieldError = (fieldName: string) => {
    const fieldState = getFieldState(fieldName as any);
    return fieldState.error;
  };

  const storeOffersOptions = useMemo(() => {
    return {
      [OfferType.BUNDLE]: storeOffers?.[OfferType.BUNDLE]?.map(
        ({ offerId, name }) => ({
          value: offerId,
          content: name || offerId
        })
      ),
      [OfferType.SPECIAL_OFFER]: storeOffers?.[OfferType.SPECIAL_OFFER]?.map(
        ({ offerId, name }) => ({
          value: offerId,
          content: name || offerId
        })
      ),
      [OfferType.ROLLING_OFFER]: storeOffers?.[OfferType.ROLLING_OFFER]?.map(
        ({ offerId, name }) => ({
          value: offerId,
          content: name || offerId
        })
      )
    };
  }, [storeOffers]);

  const offerTypeOptions: InputSelectDropdownOption[] = useMemo(() => {
    return [
      ...(storeOffersOptions[OfferType.BUNDLE] &&
      storeOffersOptions[OfferType.BUNDLE]?.length > 0
        ? [
            {
              label: 'Bundles',
              value: OfferType.BUNDLE
            }
          ]
        : []),
      ...(storeOffersOptions[OfferType.SPECIAL_OFFER] &&
      storeOffersOptions[OfferType.SPECIAL_OFFER]?.length > 0
        ? [
            {
              label: 'Promotions',
              value: OfferType.SPECIAL_OFFER
            }
          ]
        : []),
      ...(storeOffersOptions[OfferType.ROLLING_OFFER] &&
      storeOffersOptions[OfferType.ROLLING_OFFER]?.length > 0
        ? [
            {
              label: 'Rolling Offers',
              value: OfferType.ROLLING_OFFER
            }
          ]
        : [])
    ];
  }, [storeOffersOptions]);

  const onOfferTypeChange = (value: OfferType) => {
    resetField(`${basePath}.offerIds`, {
      keepDirty: true,
      keepTouched: true,
      keepError: true,
      defaultValue: []
    });
  };

  const offerIdsOptions = useMemo(() => {
    const offerType = currentOfferType as
      | OfferType.BUNDLE
      | OfferType.SPECIAL_OFFER
      | OfferType.ROLLING_OFFER;

    return storeOffersOptions?.[offerType] || [];
  }, [currentOfferType, storeOffersOptions]);

  const handleRenderThemesValue = useCallback(
    (selected: any) => {
      const selectedLength = selected?.length;
      const selectedValue = offerIdsOptions.find(
        (option: any) => option.value === selected[0]
      )?.content;
      if (
        selected &&
        selectedLength < offerIdsOptions?.length &&
        selectedLength > 1
      )
        return selected
          .map(
            (item: string) =>
              offerIdsOptions.find((option: any) => option.value === item)
                ?.content
          )
          .join(', ');
      if (selectedLength === 1) return selectedValue;

      return 'All';
    },
    [offerIdsOptions]
  );

  return (
    <div
      className={
        'flex flex-col gap-4 p-4 rounded-md border transition-colors border-base-border'
      }
    >
      <FormField
        control={control}
        name={`${basePath}.offerType`}
        render={({
          field: { onChange }
        }: {
          field: OfferFieldProps<'offerType'>;
        }) => (
          <FormItem>
            <FormLabel>* Offer Type</FormLabel>
            <FormControl>
              <InputSelectDropdown
                label=""
                defaultSelected={
                  offerTypeOptions.find(
                    (option) =>
                      option.value === (currentOfferType || OfferType.BUNDLE)
                  ) || offerTypeOptions[0]
                }
                options={offerTypeOptions}
                onOptionChange={(value) => {
                  onChange(value);
                  onOfferTypeChange(value as OfferType);
                }}
                iconClassName="text-xs text-base-mutedForeground"
                placeholder="Select offer type"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${basePath}.offerIds`}
        render={({
          field: { onChange, value }
        }: {
          field: OfferFieldProps<'offerIds'>;
        }) => (
          <FormItem>
            <FormLabel>* Offer Names</FormLabel>
            <FormControl>
              <div className="w-full text-xs">
                <AcSelect
                  items={offerIdsOptions}
                  isCheckBox
                  isPlaceholderBold
                  isSearchEnabled
                  searchPlaceholder="Search project name or ID"
                  allowSearchByContent
                  setSelectedItems={onChange}
                  value={value || []}
                  renderValue={handleRenderThemesValue}
                  isApplyBtnEnabled
                  selectFieldClassName={'h-[40px]'}
                  className={cn(
                    getFieldError(`${basePath}.offerIds`)?.message
                      ? ERROR_FIELD_CLASS
                      : ''
                  )}
                  IconComponent={ChevronDownIcon}
                />
              </div>
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <BlockVisibilityField basePath={basePath} disabled />
      <BlockSegmentsField basePath={basePath} />
    </div>
  );
};
